import {
  DEFAULT_AXIS_FONT_SIZE,
  DEFAULT_AXIS_LABEL_FONT_SIZE,
  DEFAULT_LEGEND_FONT_SIZE,
  DEFAULT_TITLE_FONT_SIZE
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { resolveFontFamily } from "constants/style.constants";
import type { EChartsType } from "echarts";
import { DEFAULT_TITLE_OPTIONS } from "entities/charts";
import {
  deriveScreenshotMpcXAxisLegends,
  preserveAxisLegendSuffix,
  stripAxisLegendSuffix
} from "entities/charts/eva/translators/mpcAxisLegend";
import {
  buildProductLegendData,
  buildProductLegendGraphic
} from "entities/charts/eva/translators/mpcProductLegend";
import { formatChartTitleWithSubscript } from "entities/charts/eva/translators/utils";
import formatPropertyName from "utils/column/formatPropertyName";
import { applyFieldNameMode } from "utils/column/getColumnLabelWithContext";

import { IChartScreenshotSettings } from "models/chart";
import { IGroupByListColumn } from "models/groupBy";
import { IScreenshotAbbreviation } from "models/screenshot";

import { applyAbbreviations } from "components/screenshot/utils";

import { adjustXAxisForChart } from "./applyChartSpecificAdjustmentsForXAxis";
import applySpacingToChartOptions from "./applySpacingToChartOptions";
import {
  getInnermostYAxisIndex,
  getSortedXAxisIndices,
  getSortedYAxisIndices
} from "./getSortedAxisIndices";

/**
 * EVA-127 "Simplify Custom Field Names": property → column lookup plus the x/y
 * field properties, so the single-axis default (non-override) name can be
 * simplified or restored the same way qualifyAxisName does on the live render
 * path. Optional and additive — callers that don't pass this (SAM,
 * multiphase-chart) are unaffected; those pipelines don't wire the toggle in
 * the first place.
 */
export interface ChartScreenshotFieldContext {
  columnsDictionary?: Record<string, IGroupByListColumn>;
  xFieldProperty?: Maybe<string>;
  yFieldProperty?: Maybe<string>;
}

/**
 * Get the specific screenshot options for a chart.
 *
 * @param any options The chart options
 * @param IChartScreenshotSettings screenshotSettings The screenshot settings
 * @returns any The chart options for screenshots
 */
const getChartScreenshotOptions = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any,
  screenshotSettings: IChartScreenshotSettings,
  chartInstance: EChartsType,
  abbreviations: IScreenshotAbbreviation[] = [],
  applyAbbreviationsOnXAxis?: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  originalOptions?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiResponse?: any,
  isMultiProduct?: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  multiProduct?: any,
  fieldContext?: ChartScreenshotFieldContext
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  if (!options || !screenshotSettings) {
    return options;
  }

  const { columnsDictionary, xFieldProperty, yFieldProperty } = fieldContext ?? {};
  const xColumn = xFieldProperty
    ? columnsDictionary?.[formatPropertyName(xFieldProperty)]
    : undefined;
  const yColumn = yFieldProperty
    ? columnsDictionary?.[formatPropertyName(yFieldProperty)]
    : undefined;

  // The lean WIDGET charts — Rig/Frac (EVA-76) and Well Status — emit
  // their OWN fixed grid + size-aware axis nameGaps (rotated date labels,
  // measured category-label widths) that the wells-oriented grid/nameGap/spacing
  // overrides below would mangle: applied to Well Status they pushed the plot off
  // its own axes during capture ("the spacing can be off"). Skip just
  // those overrides here; every other screenshot feature (font sizes,
  // title/axis text, grid-line toggles, font family/weight) still applies.
  // Guarded on chartType, so wells behaviour is unchanged.
  const isLeanWidgetChart =
    options.chartType === EvaChart.RigFracActivity ||
    options.chartType === EvaChart.RigFracRecentActivity ||
    options.chartType === EvaChart.WellStatusOverTime ||
    options.chartType === EvaChart.WellStatusCurrent;
  // Activity vs Time is a non-multi-product chart with a real secondary y-axis,
  // so it drives the per-axis title + gridline controls (AxisSettingsList) the
  // same way multi-product does.
  const isRigFracActivity = options.chartType === EvaChart.RigFracActivity;

  // If the chart type is Stacked Bar, Pie or BoxPlot, we need to apply abbreviations on the xAxis data
  if (screenshotSettings?.applyAbbreviations && applyAbbreviationsOnXAxis) {
    if (Array.isArray(options.series)) {
      options.series.forEach((series) => {
        const isViolinSeries = series.customType === "violin";
        series.data.forEach((dataItem) => {
          if (dataItem.name) {
            dataItem.name = applyAbbreviations(dataItem.name, abbreviations);
          }
          // for jitter plot because dataItem is an array, first one is the name
          if (Array.isArray(dataItem) && dataItem.length > 0) {
            dataItem[0] = applyAbbreviations(dataItem[0], abbreviations);
          }
          // violin custom series datum is object-shaped ({ value: [category] });
          // abbreviate the category so it stays in sync with the renamed ordinal axis.
          // Gate on the violin series specifically: other custom series (Pie/BoxPlot/
          // StackedBar) may carry a different value[0] contract that must not be rewritten.
          if (
            isViolinSeries &&
            dataItem &&
            !Array.isArray(dataItem) &&
            Array.isArray(dataItem.value) &&
            typeof dataItem.value[0] === "string"
          ) {
            dataItem.value[0] = applyAbbreviations(dataItem.value[0], abbreviations);
          }
        });
      });
    }
    if (Array.isArray(options.xAxis[0]?.data)) {
      options.xAxis[0].data = options.xAxis[0].data.map((item) =>
        applyAbbreviations(item, abbreviations)
      );
    }
  } else {
    options = originalOptions || options;
  }

  const getScreenshotSetting = (input: number, defaultValue: number): number =>
    isNaN(+input) || !input ? defaultValue : +input;

  const axisTitleSize =
    screenshotSettings.showAxisTitle === false
      ? 0
      : getScreenshotSetting(screenshotSettings.axisFontSize, 12);
  const axisLabelSize =
    screenshotSettings.showAxisLabels === false
      ? 0
      : getScreenshotSetting(screenshotSettings.axisLabelFontSize, 12);
  const titleFontSize = getScreenshotSetting(screenshotSettings.titleFontSize, 18);
  const axisFontWeight =
    screenshotSettings.axisFontWeight !== undefined
      ? screenshotSettings.axisFontWeight
      : "normal";

  // Rig/Frac spacing: its fixed grid + nameGaps are sized for the default live
  // fonts, and it skips applySpacingToChartOptions (which re-measures the wells
  // layouts off the chart instance). Re-space STRUCTURALLY instead, mirroring
  // the wells model (inset = axis nameGap + axis-title height; top = chart-title
  // height + padding): the nameGap scales with the LABEL size (the gap exists to
  // clear the labels), and each grid inset then grows/shrinks by exactly how
  // much its nameGap and its title's line height changed vs the live defaults.
  // A flat ratio on the whole inset either starves a large axis title (labels
  // unchanged → inset unchanged → 40pt titles clipped out of frame) or
  // over-reserves (title ratio applied to the whole top inset).
  const rigFracLabelRatio = Math.max(axisLabelSize / DEFAULT_AXIS_LABEL_FONT_SIZE, 0.5);
  const scaleRigFracWidth = (width: number): number =>
    Math.max(8, Math.round(width * rigFracLabelRatio));
  // Effective rig/frac axis nameGap at the chosen label size.
  //
  // A category axis whose labels are truncated to a fixed pixel width (Recent
  // Activity's Operator axis: nameGap 92 = echarts label margin + label width 76
  // + slack) must scale ONLY the label-width portion and keep the constant
  // margin+slack. Scaling the whole gap collapses the clearance to ~0 at small
  // fonts, so the rotated title sits flush against the labels — the very
  // whitespace/overlap complaint, back in the other direction (EVA-76 QA). Axes
  // without a fixed label width scale the whole gap (their labels genuinely
  // shrink with the font, so there's no fixed constant to protect).
  const rigFracNameGap = (axis): number => {
    const width = axis?.axisLabel?.width;
    return typeof width === "number"
      ? // Same 16px floor as the whole-gap branch below: the current 92/76 pair
        // is comfortably positive, but a future axis with nameGap <= label width
        // must never collapse to a zero/negative gap.
        Math.max(16, axis.nameGap - width + scaleRigFracWidth(width))
      : Math.max(16, Math.round(axis.nameGap * rigFracLabelRatio));
  };

  // How far a ROTATED x-axis label reaches horizontally past the tick it hangs
  // from. ECharts anchors a `rotate: 45` label at its tick and runs the text
  // down-and-LEFT, so the FIRST bucket's label overhangs the plot's left edge —
  // straight over the y-axis, its labels and its title, which is exactly the
  // "slanted labels are overlapping with y axis" report (product; the same
  // applies to rig/frac Activity vs Time). The grid's left inset is sized
  // from the Y-axis's own needs, so it knows nothing about this; widening it to
  // cover the overhang is what separates them.
  //
  // Mirrors the translator's `rotatedLabelExtent` estimate — longest label ×
  // ~0.5em per char, plus one line height — projected onto the horizontal axis.
  // At 45° the horizontal and vertical extents are equal, which is why the same
  // estimate serves the nameGap (vertical) and this inset (horizontal).
  const rotatedXLabelOverhang = (axis): number => {
    const rotate = axis?.axisLabel?.rotate;
    if (!rotate || !Array.isArray(axis?.data)) return 0;
    const longest = axis.data.reduce(
      (max: number, label) => Math.max(max, String(label ?? "").length),
      0
    );
    if (longest === 0) return 0;
    const textWidth = longest * axisLabelSize * 0.5;
    const radians = (Math.abs(rotate) * Math.PI) / 180;
    return Math.ceil(Math.cos(radians) * (textWidth + axisLabelSize * 1.2));
  };

  const getAxis = (axis, titleGap: number) => {
    const axisLabel = { ...axis.axisLabel, fontSize: axisLabelSize };
    // Scale the fixed truncation width with the label size so the left block
    // tightens as the font shrinks instead of leaving a wide empty margin;
    // rigFracNameGap keeps the (constant) title clearance on top of it.
    if (isLeanWidgetChart && typeof axis?.axisLabel?.width === "number") {
      axisLabel.width = scaleRigFracWidth(axis.axisLabel.width);
    }
    return {
      ...axis,
      axisLabel,
      // Clone splitLine so the per-axis gridline writes further down
      // (screenshotOptions.*.splitLine.show = …) mutate a fresh object, not the
      // axis's shared reference. For any axis built via createStateWithDefaults
      // without a splitLine override, that reference IS the module-level
      // DEFAULT_*_AXIS_OPTIONS.splitLine (a shallow { ...default, ...overrides }
      // merge) — writing `show` onto it contaminated every later axis in the
      // session and leaked the screenshot value into the persisted options JSON.
      ...(axis?.splitLine && { splitLine: { ...axis.splitLine } }),
      // Rig/Frac keeps its own size-aware nameGap; other charts use the preset gap.
      nameGap:
        isLeanWidgetChart && axis.nameGap != null ? rigFracNameGap(axis) : titleGap,
      nameTextStyle: {
        ...axis?.nameTextStyle,
        fontSize: axisTitleSize,
        fontWeight: axisFontWeight,
        rich: {
          ...axis?.nameTextStyle?.rich,
          sub: {
            ...(axis?.nameTextStyle?.rich?.sub || {}),
            fontSize: axisTitleSize * 0.8
          }
        }
      }
    };
  };

  const getXAxis = () => {
    const titleGap: number = getScreenshotSetting(screenshotSettings.nameGapX, 20);
    return Array.isArray(options.xAxis)
      ? options.xAxis.map((axis) => getAxis(axis, titleGap))
      : getAxis(options.xAxis, titleGap);
  };

  /**
   * Automatic adjustment for axis titles, and labels, have not yet been implemented.
   * https://github.com/apache/echarts/issues/9265
   *
   * There is no specific answer for the padding/multiplier values, created to be dynamic in some manner.
   * Label size used for a scaling metric in grid padding, the title is accurate to simply add.
   * Did not explore, but the labels maximum pixel width would be accurate, and could then just be added.
   * Adjust the multiplier/title-gap padding if the label is overlapping the axis title.
   * Right-side axis potentially requires more grid-padding/title-gap because of the axis label's trailing numbers overlapping the axis title
   */
  const getYAxis = () => {
    const nameGap: number = getScreenshotSetting(screenshotSettings.nameGapY, 20);

    const getMultipleYAxis = () => options.yAxis.map((axis) => getAxis(axis, nameGap));

    return Array.isArray(options.yAxis)
      ? getMultipleYAxis()
      : getAxis(options.yAxis, nameGap);
  };

  const removeDataZoomSlider = () =>
    options.dataZoom.map((dataZoom) => ({ ...dataZoom, show: false }));

  const screenshotOptions = {
    ...options,
    ...(options.dataZoom && { dataZoom: removeDataZoomSlider() }),
    // in the new chart, we get the options from the chart instance
    // which doesn't have the default title options
    ...(Array.isArray(options.title)
      ? {
          title: {
            ...DEFAULT_TITLE_OPTIONS,
            text: options.title[0].text,
            // Keep the box plot's Data Range subtitle, which DEFAULT_TITLE_OPTIONS blanks.
            subtext: options.title[0].subtext ?? DEFAULT_TITLE_OPTIONS.subtext,
            itemGap: options.title[0].itemGap ?? DEFAULT_TITLE_OPTIONS.itemGap,
            textStyle: {
              ...options.title[0].textStyle,
              fontSize: titleFontSize,
              rich: {
                ...(options.title[0].textStyle?.rich || {}),
                sub: {
                  ...(options.title[0].textStyle?.rich?.sub || {}),
                  fontSize: titleFontSize * 0.8
                }
              }
            }
          }
        }
      : options.title && {
          title: {
            ...options.title,
            textStyle: {
              ...options.title.textStyle,
              fontSize: titleFontSize,
              rich: {
                ...(options.title.textStyle?.rich || {}),
                sub: {
                  ...(options.title?.textStyle?.rich?.sub || {}),
                  fontSize: titleFontSize * 0.8
                }
              }
            }
          }
        }),
    ...(options.textStyle && {
      textStyle: {
        ...options.textStyle,
        fontSize: axisLabelSize
      }
    }),
    ...(options.xAxis && { xAxis: getXAxis() }),
    ...(options.yAxis && { yAxis: getYAxis() }),
    // Rig/Frac keeps its own fixed grid, RE-SPACED structurally per the chosen
    // font sizes (see the model note above): each inset moves by the change in
    // its axis nameGap plus the change in the relevant title's line height.
    // Other charts take the preset grid.
    ...(options.grid && {
      grid: isLeanWidgetChart
        ? (() => {
            const titleHeight = (size: number) => Math.round(size * 1.2);
            // The translator sizes axis titles at DEFAULT_AXIS_FONT_SIZE and the
            // chart title at DEFAULT_TITLE_FONT_SIZE — the baselines the fixed
            // grid was built around.
            const axisTitleDelta =
              titleHeight(axisTitleSize) - titleHeight(DEFAULT_AXIS_FONT_SIZE);
            const chartTitleDelta =
              titleHeight(titleFontSize) - titleHeight(DEFAULT_TITLE_FONT_SIZE);
            const gapDelta = (axis) =>
              axis?.nameGap != null ? rigFracNameGap(axis) - axis.nameGap : 0;
            const yList = Array.isArray(options.yAxis) ? options.yAxis : [options.yAxis];
            const leftY = yList.find((a) => a && a.position !== "right");
            const rightY = yList.find((a) => a && a.position === "right");
            const x = Array.isArray(options.xAxis) ? options.xAxis[0] : options.xAxis;
            return {
              ...options.grid,
              top: Math.max(0, (options.grid.top ?? 0) + chartTitleDelta),
              // The x-axis title is centred `nameGap` below the axis line, so the
              // bottom band must hold that gap PLUS the title's own line height
              // or the title renders inside the label band (or clipped off the
              // frame). The structural delta below is usually enough; the floor
              // is what guarantees it when the title font is bumped far past the
              // label font.
              bottom: Math.max(
                0,
                (options.grid.bottom ?? 0) + gapDelta(x) + axisTitleDelta,
                x?.nameGap != null ? rigFracNameGap(x) + titleHeight(axisTitleSize) : 0
              ),
              // Whichever is larger: the room the Y-AXIS needs (its nameGap +
              // title), or the room the first rotated X label overhangs into.
              // Taking the max rather than the sum keeps the plot as wide as
              // possible — the two compete for the same inset, they don't stack.
              left: Math.max(
                0,
                (options.grid.left ?? 0) + gapDelta(leftY) + axisTitleDelta,
                rotatedXLabelOverhang(x)
              ),
              // The right inset only hosts a title when a right axis exists
              // (Activity's y2); Recent Activity's slim right inset stays put.
              right: Math.max(
                0,
                (options.grid.right ?? 0) +
                  (rightY ? gapDelta(rightY) + axisTitleDelta : 0)
              )
            };
          })()
        : {
            left: screenshotSettings.gridLeft,
            right: screenshotSettings.gridRight,
            top: screenshotSettings.gridTop,
            bottom: screenshotSettings.gridBottom
          }
    })
  };

  if (screenshotSettings?.titleText && screenshotOptions.title) {
    screenshotOptions.title.text = screenshotSettings?.applyAbbreviations
      ? applyAbbreviations(screenshotSettings?.titleText, abbreviations)
      : screenshotSettings?.titleText;
  }
  if (screenshotSettings?.showDataRangeSubtitle === false && screenshotOptions.title) {
    screenshotOptions.title.subtext = "";
  }
  // The beside-axis (x-axis swatch) legend is built ONLY by the cum translators
  // (rateCum/totalRateCum via resolveMpcXAxisName). Time-based charts keep a plain x-axis
  // (e.g. "Date") and show the bottom graphic legend instead — so both the x-axis swatch
  // re-derive and the bottom-legend rebuild below key off this (EVA-6766).
  const isCumChart =
    screenshotOptions.chartType === EvaChart.RateCum ||
    screenshotOptions.chartType === EvaChart.TotalRateCum;

  const axisSettings = screenshotSettings as Record<string, unknown>;
  if (screenshotOptions.xAxis) {
    if (screenshotOptions.xAxis.length > 0) {
      // Re-derive MPC x-axis titles + swatches from the backend layout using the
      // screenshot's OWN product-legend setting, overwriting whatever the captured
      // snapshot held (which is frozen at the chart's legend state). This decouples the
      // screenshot title from the chart render without mutating the chart — so toggling
      // the legend can't flash an intermediate state. Cum charts only: time-based charts
      // can also carry an xAxis2 in their layout but must NOT get x-axis swatches — their
      // x-axis stays plain (e.g. "Date") (EVA-6766).
      if (isMultiProduct && isCumChart && apiResponse?.layout?.xAxis2?.length) {
        const mpcLegends = deriveScreenshotMpcXAxisLegends(
          apiResponse.layout.xAxis2,
          apiResponse.series,
          multiProduct,
          screenshotSettings?.showProductLegend !== false
        );
        screenshotOptions.xAxis.forEach((axis, idx) => {
          const derived = mpcLegends[typeof axis.id === "number" ? axis.id : idx];
          if (!derived) return;
          // derived.name is already formatChartTitleWithSubscript-ed by the helper.
          axis.name = derived.name;
          // Drop stale swatch rich (swN/swrN) from the snapshot, keep any other rich
          // (e.g. subscript styles), then add the freshly-derived swatches.
          const baseRich: Record<string, unknown> = {};
          for (const k of Object.keys(axis.nameTextStyle?.rich ?? {})) {
            if (!/^swr?\d+$/.test(k)) baseRich[k] = axis.nameTextStyle.rich[k];
          }
          axis.nameTextStyle = {
            ...axis.nameTextStyle,
            rich: { ...baseRich, ...(derived.rich ?? {}) }
          };
        });
      }
      const sortedXIndices = getSortedXAxisIndices(screenshotOptions.xAxis);
      sortedXIndices.forEach((axisIdx, rank) => {
        const n = rank === 0 ? "" : `${rank + 1}`;
        const titleText = axisSettings[`xAxis${n}TitleText`] as string | undefined;
        if (titleText && screenshotOptions.xAxis[axisIdx]) {
          screenshotOptions.xAxis[axisIdx].name = preserveAxisLegendSuffix(
            screenshotSettings?.applyAbbreviations
              ? applyAbbreviations(titleText, abbreviations)
              : titleText,
            screenshotOptions.xAxis[axisIdx].name
          );
        } else if (screenshotOptions.xAxis.length === 1 && xColumn) {
          // EVA-127 "Simplify Custom Field Names": custom-field-only, and only
          // for a genuinely single-axis chart — a multi-axis chart's extra axes
          // are per-product (Oil/Gas/Water rate, …), not per-column, so there is
          // no column to resolve for them (mirrors qualifyAxisName's bail-out on
          // Array.isArray(axis)). No explicit user title, so the toggle applies.
          // This is the array shape: getOption() always normalizes axes into an
          // array (useChartScreenshotOptions.ts), so this — not the object branch
          // below — is the path a real screenshot capture takes.
          screenshotOptions.xAxis[axisIdx].name = applyFieldNameMode(
            screenshotOptions.xAxis[axisIdx].name,
            xColumn,
            screenshotSettings?.simplifyCustomFieldNames ?? false
          );
        }
      });
      if (isMultiProduct) {
        sortedXIndices.forEach((axisIdx, rank) => {
          const n = rank === 0 ? "" : `${rank + 1}`;
          const showMajor = (axisSettings[`gridShowX${n}Lines`] ?? false) as boolean;
          const showMinor = (axisSettings[`gridShowX${n}MinorLines`] ?? false) as boolean;
          if (screenshotOptions.xAxis[axisIdx].splitLine) {
            screenshotOptions.xAxis[axisIdx].splitLine.show = showMajor;
          }
          screenshotOptions.xAxis[axisIdx].minorSplitLine = {
            show: showMinor,
            lineStyle: { color: "#F2F2F2" }
          };
        });
      } else {
        const primaryXGridAxisIndex = sortedXIndices[0];
        for (let i = 0; i < screenshotOptions.xAxis.length; i++) {
          const isPrimaryXAxis = i === primaryXGridAxisIndex;
          if (
            screenshotSettings?.gridShowXLines !== undefined &&
            screenshotOptions.xAxis[i].splitLine
          ) {
            screenshotOptions.xAxis[i].splitLine.show = isPrimaryXAxis
              ? screenshotSettings.gridShowXLines
              : false;
          }
          if (screenshotSettings?.gridShowXMinorLines !== undefined) {
            screenshotOptions.xAxis[i].minorSplitLine = {
              show: isPrimaryXAxis ? screenshotSettings.gridShowXMinorLines : false,
              lineStyle: { color: "#F2F2F2" }
            };
          }
        }
      }
    } else {
      // handle single X axis
      if (screenshotSettings?.xAxisTitleText) {
        screenshotOptions.xAxis.name = preserveAxisLegendSuffix(
          screenshotSettings?.applyAbbreviations
            ? applyAbbreviations(screenshotSettings?.xAxisTitleText, abbreviations)
            : screenshotSettings?.xAxisTitleText,
          screenshotOptions.xAxis.name
        );
      }
      // EVA-127 "Simplify Custom Field Names" is NOT applied here: this object
      // branch (screenshotOptions.xAxis.length === 0, i.e. a non-array axis) is
      // never hit by a real screenshot capture — getOption() always normalizes
      // axes into an array (see useChartScreenshotOptions.ts) — so there's no
      // production shape for it to handle. The toggle is applied in the array
      // branch above instead.
      if (
        screenshotSettings?.gridShowXLines !== undefined &&
        screenshotOptions.xAxis.splitLine
      ) {
        screenshotOptions.xAxis.splitLine.show = screenshotSettings?.gridShowXLines;
      }
      if (screenshotSettings?.gridShowXMinorLines !== undefined) {
        screenshotOptions.xAxis.minorSplitLine = {
          show: screenshotSettings?.gridShowXMinorLines,
          lineStyle: { color: "#F2F2F2" }
        };
      }
    }
  }

  if (screenshotOptions.yAxis) {
    if (screenshotOptions.yAxis.length > 0) {
      const sortedIndices = getSortedYAxisIndices(screenshotOptions.yAxis);
      sortedIndices.forEach((axisIdx, rank) => {
        const n = rank === 0 ? "" : `${rank + 1}`;
        const titleText = axisSettings[`yAxis${n}TitleText`] as string | undefined;
        if (titleText && screenshotOptions.yAxis[axisIdx]) {
          screenshotOptions.yAxis[axisIdx].name = preserveAxisLegendSuffix(
            screenshotSettings?.applyAbbreviations
              ? applyAbbreviations(titleText, abbreviations)
              : titleText,
            screenshotOptions.yAxis[axisIdx].name
          );
        } else if (screenshotOptions.yAxis.length === 1 && yColumn) {
          // EVA-127 "Simplify Custom Field Names": see the matching xAxis branch
          // above — same single-axis-only gate, same array-is-the-real-shape
          // reasoning.
          screenshotOptions.yAxis[axisIdx].name = applyFieldNameMode(
            screenshotOptions.yAxis[axisIdx].name,
            yColumn,
            screenshotSettings?.simplifyCustomFieldNames ?? false
          );
        }
      });
      if (isMultiProduct || isRigFracActivity) {
        // Per-axis gridline toggles (Y Axes list). Activity vs Time drives these
        // like multi-product so its secondary (Frac Crew Count) axis gets its own
        // Major/Minor toggle rather than inheriting the primary's.
        sortedIndices.forEach((axisIdx, rank) => {
          const n = rank === 0 ? "" : `${rank + 1}`;
          const showMajor = (axisSettings[`gridShowY${n}Lines`] ?? false) as boolean;
          const showMinor = (axisSettings[`gridShowY${n}MinorLines`] ?? false) as boolean;
          if (screenshotOptions.yAxis[axisIdx].splitLine) {
            screenshotOptions.yAxis[axisIdx].splitLine.show = showMajor;
          }
          screenshotOptions.yAxis[axisIdx].minorSplitLine = {
            show: showMinor,
            lineStyle: { color: "#F2F2F2" }
          };
        });
      } else {
        const primaryGridAxisIndex = getInnermostYAxisIndex(screenshotOptions.yAxis);
        for (let i = 0; i < screenshotOptions.yAxis.length; i++) {
          const isPrimaryAxis = i === primaryGridAxisIndex;
          if (
            screenshotSettings?.gridShowYLines !== undefined &&
            screenshotOptions.yAxis[i].splitLine
          ) {
            screenshotOptions.yAxis[i].splitLine.show = isPrimaryAxis
              ? screenshotSettings.gridShowYLines
              : false;
          }
          if (screenshotSettings?.gridShowYMinorLines !== undefined) {
            screenshotOptions.yAxis[i].minorSplitLine = {
              show: isPrimaryAxis ? screenshotSettings.gridShowYMinorLines : false,
              lineStyle: { color: "#F2F2F2" }
            };
          }
        }
      }
    } else {
      // handle single Y axis
      if (screenshotSettings?.yAxisTitleText) {
        screenshotOptions.yAxis.name = screenshotSettings?.applyAbbreviations
          ? applyAbbreviations(screenshotSettings?.yAxisTitleText, abbreviations)
          : screenshotSettings?.yAxisTitleText;
      }
      // EVA-127 "Simplify Custom Field Names": see the matching xAxis object
      // branch above — not applied here for the same reason (dead in production;
      // handled in the array branch instead).
      if (
        screenshotSettings?.gridShowYLines !== undefined &&
        screenshotOptions.yAxis.splitLine
      ) {
        screenshotOptions.yAxis.splitLine.show = screenshotSettings?.gridShowYLines;
      }
      if (screenshotSettings?.gridShowYMinorLines !== undefined) {
        screenshotOptions.yAxis.minorSplitLine = {
          show: screenshotSettings?.gridShowYMinorLines,
          lineStyle: { color: "#F2F2F2" }
        };
      }
    }
  }
  if (screenshotSettings?.fontFamily && screenshotOptions.textStyle) {
    screenshotOptions["textStyle"]["fontFamily"] = resolveFontFamily(
      screenshotSettings?.fontFamily
    );
  }

  if (screenshotSettings?.showProductLegend === false) {
    if (Array.isArray(screenshotOptions.xAxis)) {
      screenshotOptions.xAxis.forEach((axis) => {
        if (axis.name) axis.name = stripAxisLegendSuffix(axis.name);
      });
    } else if (screenshotOptions.xAxis?.name) {
      screenshotOptions.xAxis.name = stripAxisLegendSuffix(screenshotOptions.xAxis.name);
    }
    if (Array.isArray(screenshotOptions.yAxis)) {
      screenshotOptions.yAxis.forEach((axis) => {
        if (axis.name) axis.name = stripAxisLegendSuffix(axis.name);
      });
    } else if (screenshotOptions.yAxis?.name) {
      screenshotOptions.yAxis.name = stripAxisLegendSuffix(screenshotOptions.yAxis.name);
    }
  }

  // MPC product-legend graphic (the bottom swatch row). Rebuild it from multiProduct
  // using the screenshot's OWN toggle, as a fresh clean group; NEVER reuse the captured
  // snapshot's graphic. getOption() returns graphic in a normalized form, and feeding
  // that back (modified) through setOption corrupts ECharts' coordsys/dataZoom
  // bookkeeping and crashes — whereas a fresh group def (exactly what the live chart
  // emits via applyProductLegend) is safe. The product legend is the only graphic these
  // charts produce, so replacing wholesale loses nothing, and applySpacing below reads
  // the result so the reserved bottom band matches the toggle either way.
  //
  // Gate on the ABSENCE of a beside-axis (x-axis) legend. The beside-axis swatch legend is
  // a CUM-chart feature: combined cum charts (RateCum/TotalRateCum with per-product x-axes)
  // carry the legend in their axis names, so they must NOT also get the bottom legend.
  // Every other MPC chart uses the bottom legend — time-based charts (rateDate/rateTime/
  // cumTime, which call applyProductLegend unconditionally, and can still carry an xAxis2
  // in their layout) and single-axis cum. This mirrors the translators' own gates exactly
  // (cum: `if (!layout.xAxis2?.length) applyProductLegend`; time: unconditional) (EVA-6766).
  if (isMultiProduct) {
    const usesBesideAxisLegend = isCumChart && !!apiResponse?.layout?.xAxis2?.length;
    const legend =
      !usesBesideAxisLegend &&
      screenshotSettings?.showProductLegend !== false &&
      multiProduct
        ? buildProductLegendGraphic(buildProductLegendData(multiProduct))
        : null;
    screenshotOptions.graphic = legend ? [legend] : [];
  }

  const screenshotOptions2 = {
    ...screenshotOptions,
    // Rig/Frac has a fixed grid and no MPC legend / log buttons, so skip the
    // dynamic spacing pass — it re-derives grid/nameGap from the wells layout and
    // would undo the rig/frac grid kept above.
    ...(isLeanWidgetChart
      ? {}
      : applySpacingToChartOptions(
          screenshotOptions,
          chartInstance,
          {
            titleFontSize: screenshotSettings.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE,
            axisFontSize: screenshotSettings.axisFontSize ?? DEFAULT_AXIS_FONT_SIZE,
            axisLabelFontSize:
              screenshotSettings.axisLabelFontSize ?? DEFAULT_AXIS_LABEL_FONT_SIZE,
            legendFontSize: screenshotSettings.legendFontSize ?? DEFAULT_LEGEND_FONT_SIZE,
            fontFamily: resolveFontFamily(screenshotSettings.fontFamily)
          },
          false,
          true
        )),
    backgroundColor: "",
    title: {
      ...screenshotOptions.title,
      text: formatChartTitleWithSubscript(screenshotOptions?.title?.text),
      textStyle: {
        ...screenshotOptions.title?.textStyle
      }
    },
    xAxis: Array.isArray(screenshotOptions.xAxis)
      ? screenshotOptions.xAxis.map((axis) => ({
          ...axis,
          name: formatChartTitleWithSubscript(axis?.name)
        }))
      : {
          ...screenshotOptions.xAxis,
          name: formatChartTitleWithSubscript(screenshotOptions.xAxis?.name)
        },
    yAxis: Array.isArray(screenshotOptions.yAxis)
      ? screenshotOptions.yAxis.map((axis) => ({
          ...axis,
          name: formatChartTitleWithSubscript(axis?.name)
        }))
      : {
          ...screenshotOptions.yAxis,
          name: formatChartTitleWithSubscript(screenshotOptions.yAxis?.name)
        }
  };

  // chart specific adjustments for x axis labels (wells charts; rig/frac's rotated
  // date axis is already handled by its translator).
  if (!isLeanWidgetChart) {
    adjustXAxisForChart(screenshotOptions2, screenshotSettings, apiResponse, options);
  }

  // Rig/Frac abbreviations (Use Abbreviations) — applied LAST, on fresh objects
  // assigned only onto the returned options, so the caller's live options stay
  // un-abbreviated (useRigFracChartOptions persists liveOptions.xAxis/yAxis as
  // the true axis config). Targets GROUP names only: the stacked-segment series
  // names for Activity vs Time, and the category (y) axis labels for Recent
  // Activity's horizontal bars. The Activity date-bucket x-axis is deliberately
  // never touched — a user rule on e.g. "Jan"/"2025" would otherwise rewrite the
  // date labels.
  if (screenshotSettings?.applyAbbreviations && isLeanWidgetChart) {
    const abbreviate = (value: unknown) =>
      typeof value === "string" ? applyAbbreviations(value, abbreviations) : value;

    if (Array.isArray(screenshotOptions2.series)) {
      screenshotOptions2.series = screenshotOptions2.series.map((s) => ({
        ...s,
        name: abbreviate(s.name)
      }));
    }

    // Only Recent Activity carries groups on a category axis (its y-axis);
    // Activity's category axis is the date x-axis, which must stay verbatim.
    if (options.chartType === EvaChart.RigFracRecentActivity) {
      const yAxis = screenshotOptions2.yAxis;
      if (
        yAxis &&
        !Array.isArray(yAxis) &&
        yAxis.type === "category" &&
        Array.isArray(yAxis.data)
      ) {
        screenshotOptions2.yAxis = { ...yAxis, data: yAxis.data.map(abbreviate) };
      }
    }
  }

  return screenshotOptions2;
};

export default getChartScreenshotOptions;
